<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import Tabs from 'primevue/tabs'
import Tab from 'primevue/tab'
import TabList from 'primevue/tablist'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import Message from 'primevue/message'
import ApiKeyForm from '../forms/ApiKeyForm.vue'
import OneInchAddForm from '../forms/OneInchAddForm.vue'
import { ref, computed, watch } from 'vue'
import { useApiKeysStore } from '@/stores/apiKeysStore'
import type { availableProps } from '../forms/ApiKeyForm.vue'
import type { initValueType } from '../forms/ApiKeyForm.vue'
import type { ApiKeyType } from '@/services/api-keys-v2-service/types'
import { CEX_EXCHANGES, DEX_CEX_PRO_EXCHANGES } from '@/utils/consts'
import { useAuthStore } from '@/stores/authStore'
import JupiterAddForm from '../forms/JupiterAddForm.vue'

// import { useCheckUserRights } from '@/composable/useCheckUserRights'
// const { isDexCexAdvanced, isCexCexAdvanced } = useCheckUserRights()
const apiKeysStore = useApiKeysStore()

const totalExs = ref<Record<string, availableProps>>({
  Mexc: {
    id: 'mexc',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
    webToken: {
      show: true,
    },
    userAgent: {
      show: true,
    },
  },
  Ourbit: {
    id: 'ourbit',
    webToken: {
      show: true,
    },
    fingerPrint: {
      show: true,
    },
    userAgent: {
      show: true,
    },
  },
  LBank: {
    id: 'lbank',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
    webToken: {
      show: true,
    },
    deviceId: {
      show: true,
    },
    userAgent: {
      show: true,
    },
  },
  Gate: {
    id: 'gate',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
  },
  Bybit: {
    id: 'bybit',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
  },
  Bitrue: {
    id: 'bitrue',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
    webToken: {
      show: true,
    },
    userAgent: {
      show: true,
    },
  },
  Binance: {
    id: 'binance',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
  },
  KuCoin: {
    id: 'kucoin',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
    password: {
      show: true,
    },
  },
  BingX: {
    id: 'bingx',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
    deviceId: {
      show: true,
    },
    platformId: {
      show: true,
    },
    appVersion: {
      show: true,
    },
    antiDeviceId: {
      show: true,
    },
    appId: {
      show: true,
    },
    webToken: {
      show: true,
    },
    userAgent: {
      show: true,
    },
    deviceBrand: {
      show: true,
    },
    userId: {
      show: true,
    },
  },
  Bitget: {
    id: 'bitget',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
    password: {
      show: true,
    },
  },
  Aster: {
    id: 'aster',
    apiKey: {
      show: true,
    },
    secret: {
      show: true,
    },
  },
  Hyperliquid: {
    id: 'hyperliquid',
    apiKey: {
      show: true,
      label: 'modal.apiKeys.walletAddress',
    },
    secret: {
      show: true,
      label: 'modal.apiKeys.privateKey',
    },
  },
  "1inch": {
    id: '1inch',
    apiKey: {
      show: true,
    },
    // evmAddress: {
    //   show: true
    // },
    // solAddress: {
    //   show: true
    // },
  },
  "Jupiter": {
    id: 'jupiter',
    // evmAddress: {
    //   show: true
    // },
    // solAddress: {
    //   show: true
    // },
  },
})
const chosenEx = ref('Mexc')
const chosenProfile = ref(0)
const initValues = ref<Record<string, Record<number, initValueType>>>({})
const visible = ref(false)
const authStore = useAuthStore()
const props = defineProps<{
  pro?: boolean
  keysType: ApiKeyType
}>()

const keysTypeStr = computed(() => {
  if (props.keysType == 'cex_cex') {
    return ' (Cex-Cex)'
  } else if (props.keysType == 'dex_cex') {
    return ' (Dex-Cex)'
  }
  return ''
})
const exs = computed(() => {
  return {
    cex_cex: CEX_EXCHANGES,
    dex_cex: DEX_CEX_PRO_EXCHANGES
  }
})

const availableExchanges = computed(() => {
  return exs.value[props.keysType]
})

const profiles = computed(() =>
  props.keysType == 'cex_cex' ? authStore.profiles.cex_cex : authStore.profiles.dex_cex,
)

const getCleanInitValues = () => {
  const results: Record<string, Record<number, initValueType>> = {}
  Object.entries(totalExs.value).map((v) => {
    const exchange = v[1].id
    results[exchange] ??= {}
    for (const num of profiles.value) {
      results[exchange][num] = {
        apiKey: '',
        secret: '',
        password: '',
        webToken: null,
        fingerPrint: null,
        proxy: null,
        userAgent: null,
      }
    }
  })
  return results
}

const exchanges = computed(() => {
  const entries = Object.entries(totalExs.value).filter((v) =>
    availableExchanges.value.includes(v[1].id),
  )
  return Object.fromEntries(entries)
})

const updateForms = () => {
  const newData: Record<string, Record<number, initValueType>> = {}
  apiKeysStore.apiKeys.forEach((x) => {
    if (x.key_type != props.keysType) {
      return
    }
    const profileNum = x.num
    const exchange = x.exchange_name

    newData[exchange] ??= {}
    newData[exchange][profileNum] = {
      apiKey: x.key ?? '',
      secret: x.secret ?? '',
      password: x.passphrase ?? null,
      webToken: x.extra?.token ?? null,
      fingerPrint: x.extra?.fingerprint ?? null,
      proxy: x.proxy ?? null,
      userAgent: x.extra?.useragent ?? null,
      deviceId: x.extra?.device_id ?? null,
      evmAddress: x.extra?.evm_address ?? null,
      solAddress: x.extra?.sol_address ?? null,
      platformId: x.extra?.platform_id ?? null,
      appVersion: x.extra?.app_version ?? null,
      antiDeviceId: x.extra?.anti_device_id ?? null,
      appId: x.extra?.app_id ?? null,
      deviceBrand: x.extra?.device_brand ?? null,
      userId: x.extra?.user_id ?? null,
      duplicate: x.duplicate_id,
    }
  })
  initValues.value = {
    ...getCleanInitValues(),
    ...newData,
  }
}
watch(
  () => apiKeysStore.apiKeys,
  () => {
    updateForms()
  },
  { deep: true }
)

const profilesIndexes = computed(() => {
  return profiles.value
})
const toggle = () => {
  visible.value = !visible.value
  if (visible.value) {
    updateForms()
  }
}
defineExpose({
  toggle,
})

const getProf = (vid: string, p: number) => {
  return !['jupiter', '1inch'].includes(vid) ? p : 0
}

</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:content="p-0" pt:header="px-4 py-2"
    :header="$t('modal.apiKeys.title') + keysTypeStr" class="mt-10 lg:mt-20 w-200">
    <div class="card flex justify-center">
      <Tabs v-model:value="chosenEx" scrollable class="w-full" lazy>
        <TabList>
          <Tab v-for="(v, k) in exchanges" :key="k" :value="k">{{ k }}</Tab>
        </TabList>
        <TabPanels pt:root:class="p-2">
          <TabPanel v-for="(v, k) in exchanges" :key="k" :value="k">
            <Tabs v-model:value="chosenProfile" scrollable lazy>
              <TabPanels>
                <TabPanel v-for="p in profilesIndexes" :key="p" :value="p">

                  <Message severity="secondary" class="mb-4">{{ $t('modal.apiKeys.whiteListIp') }}: {{
                    apiKeysStore.whiteListIp }}</Message>

                  <OneInchAddForm class="mb-4" v-if="v.id == '1inch'"></OneInchAddForm>
                  <JupiterAddForm class="mb-4" v-if="v.id == 'jupiter'"></JupiterAddForm>

                  <ApiKeyForm v-if="!['jupiter', '1inch'].includes(v.id)" v-bind="v" :key-type="props.keysType"
                    :init-values="initValues[v.id]?.[getProf(v.id, p)] ?? {}" :profile-num="getProf(v.id, p)">
                  </ApiKeyForm>

                </TabPanel>
              </TabPanels>
              <TabList v-show="!['jupiter', '1inch'].includes(v.id)">
                <Tab v-for="k in profilesIndexes" :key="k" :value="k">#{{ k + 1 }} </Tab>
              </TabList>
            </Tabs>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </AdaptiveDialog>
</template>
